<script lang="ts" setup>
import OrderRefs from "@/components/order/OrderRefs.vue";
import { computed } from "vue";
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import RouteStopTime from "@/components/times/RouteStopTime.vue";
import { RouteStatus } from "@/constants/trip";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import ReplacedChip from "@/components/chips/ReplacedChip.vue";
import BookedBy from "@/components/user/BookedBy.vue";
import { useI18n } from "vue-i18n";
import OrderLink from "@/components/links/OrderLink.vue";
import type { TruckRoute, TruckRouteStop } from "@/api/trucks/dto/truck.dto";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    route: TruckRoute;
    disabled?: boolean;
    isReplaced?: boolean;
  }>(),
  {
    disabled: false,
    isReplaced: false,
  },
);
const order = computed(() => {
  return props.route.travelOrders[0].order;
});

const firstStop = computed<TruckRouteStop>(() => props.route.routeStops[0]);
const lastStop = computed<TruckRouteStop>(() => props.route.routeStops[props.route.routeStops.length - 1]);

const routeClass = computed(() => {
  if (props.route.status === RouteStatus.inProgress || props.route.status === RouteStatus.notStarted) {
    return "bg-green-lighten-5";
  }

  if (props.disabled) {
    return "bg-grey-lighten-3 border-grey-lighter-1";
  }

  return "bg-grey-lighten-4";
});

const textDisabledClass = computed(() => {
  return {
    "text-grey": props.disabled,
  };
});
</script>

<template>
  <div :class="routeClass" class="pa-4 rounded-lg">
    <div class="d-flex justify-space-between">
      <div class="d-flex align-center">
        <order-link
          v-if="order.id"
          :custom-class="textDisabledClass"
          :order-id="order.id"
          :order-number="order.number"
        />

        <div :class="textDisabledClass" class="d-flex ml-5">
          {{ t("general.ref") }}:
          <order-refs :refs="order.refNumbers" />
        </div>

        <booked-by
          v-if="order.bookedBy && order.id"
          :booked-by="order.bookedBy"
          :disabled="disabled"
          :order-id="order.id"
          class="ml-6"
        />
      </div>

      <div class="d-flex align-center">
        <replaced-chip v-if="isReplaced" class="mr-3" />
        <router-link :to="{ name: 'trip-edit', params: { id: route.tripId } }" class="mr-6" target="_blank">
          <v-btn class="font-weight-600 text-uppercase" color="primary" density="comfortable" variant="outlined">{{
            t("trip.manage-trip")
          }}</v-btn>
        </router-link>
      </div>
    </div>

    <div class="mt-3"></div>

    <v-row class="ma-0 pa-0">
      <v-col class="pa-0" cols="6">
        <facility-info
          v-if="firstStop.facility"
          :disabled="disabled"
          :facility="firstStop.facility"
          :stop-type="firstStop.type"
        />

        <route-stop-time
          :disabled="disabled"
          :stop-timezone="firstStop.timezone"
          :time-to="firstStop.timeTo"
          :time-type="firstStop.timeType"
          class="mt-2"
        />
      </v-col>

      <v-col class="pa-0" cols="6">
        <facility-info
          v-if="lastStop.facility"
          :disabled="disabled"
          :facility="lastStop.facility"
          :stop-type="lastStop.type"
        />

        <route-stop-time
          :disabled="disabled"
          :stop-timezone="lastStop.timezone"
          :time-to="lastStop.timeTo"
          :time-type="lastStop.timeType"
          class="mt-2"
        />
      </v-col>

      <alert-message v-if="props.route.wrongEmptyMiles" class="w-100">{{ t("trip.wrong-empty-miles") }}</alert-message>
    </v-row>
  </div>
</template>

<style lang="scss" scoped></style>
